import * as L from 'leaflet';
import { ManagementMap } from './ManagementMap';
import { ManagementLayer } from './ManagementLayer';
import { ManagementControl } from './ManagementControl';
import { ManagementTime } from './ManagementTime';
import { Progressbar } from './Progressbar';
import { addZoomControlMarkup } from '../lib/utils/addZoomControlMarkup';
import { initGestureHandling } from '../plugins/initGestureHandling';
import { addClassNames } from '../lib/utils/dom';
import { I18n } from '../i18n';
import { RESET_TIMESTEPS } from '../events';
/**
 * The M.A.A.P. widget constructor.
 */
export class MAAP extends L.Evented {
    /**
     * Create a new M.A.A.P. instance.
     * @param options - The M.A.A.P. constructor options.
     */
    constructor(options) {
        super();
        /**
         * The M.A.A.P widget options.
         */
        this.options = {
            parentNode: null,
            language: 'de',
            map: {},
            time: {},
            layers: {},
            controls: {},
        };
        this.options = Object.assign(Object.assign({}, this.options), options);
        this.time = new ManagementTime([], 0, this, {});
        this.i18n = new I18n(this.options.language);
        this._container = document.createElement('div');
        this._container.id = 'maap-container';
        this._container.classList.add('maap_p-maap-container');
        this._widgetContainer = document.createElement('div');
        addClassNames(this._widgetContainer, ['maap_p-maap', 'maap_s-maap']);
        this._container.appendChild(this._widgetContainer);
        this.progressbar = new Progressbar(this._widgetContainer);
        this.map = new ManagementMap(this, this.options.map);
        this.layers = new ManagementLayer(this, this.options.layers);
        this.controls = new ManagementControl(this, this.options.controls);
        if (this.options.parentNode) {
            this.appendTo(this.options.parentNode);
        }
        this.addCustomMarkup();
        initGestureHandling(this);
    }
    /**
     * Append the widget to a DOM node.
     * @param containerQuery - The DOM node to which the widget should be appended.
     */
    appendTo(containerQuery) {
        const container = L.DomUtil.get(containerQuery);
        if (container instanceof HTMLElement) {
            this.options.parentNode = container;
            container.appendChild(this._container);
            this.map._map.invalidateSize(false);
            if (this.options.map.center && this.options.map.zoom) {
                this.map._map.setView(this.options.map.center, this.options.map.zoom);
            }
        }
    }
    /**
     * Removes the widget from the DOM and destroys all controllers.
     */
    destroy() {
        L.DomUtil.remove(this._container);
        this.map.destroy();
        this.layers.destroy();
        this.controls.destroy();
    }
    /**
     * Add layers
     * @param layers - Instanced layers
     */
    addLayers(layers) {
        layers.forEach((layer) => {
            this.layers.addLayer(layer);
            let isInitiallyShown = true;
            if (layer.options.isInitiallyShown !== undefined) {
                isInitiallyShown = layer.options.isInitiallyShown;
            }
            if (isInitiallyShown)
                this.layers.showLayer(layer.options.name);
        });
    }
    /**
     * set
     * @param timesteps - time steps available
     * @param initialIndex - index in `timesteps` to be selected initially
     * @fires RESET_TIMESTEPS
     */
    setTimesteps(timesteps, initialIndex) {
        this.time.destroy();
        this.time = new ManagementTime(timesteps, initialIndex, this, this.options.time);
        this.fire(RESET_TIMESTEPS);
    }
    /**
     * Add controls and run helper functions
     * @param controls - Instanced controls
     */
    addControls(controls) {
        controls.forEach((control) => {
            this.controls.addControl(control);
            if (typeof control.addCustomMarkup === 'function')
                control.addCustomMarkup(this);
        });
    }
    /**
     * Add advertisements after widget.
     * @param advertisements - Advertisement(s) HTML Markup
     */
    addAdvertisements(advertisements) {
        // add modifier class to widget container
        this._widgetContainer.classList.add('has-advertisements');
        // create advertisements container
        const advertisementsEl = document.createElement('div');
        // specify CSS class and add it to container
        const advertisementsClassName = 'maap_p-advertisements';
        advertisementsEl.classList.add(advertisementsClassName);
        // assemble markup and set it
        let advertisementsMarkup = `<ul class="${advertisementsClassName}__list">`;
        advertisements.forEach((markup) => {
            advertisementsMarkup += `<li class="${advertisementsClassName}__advertisement">${markup}</li>`;
        });
        advertisementsMarkup += '</ul>';
        advertisementsEl.innerHTML = advertisementsMarkup;
        // add it to the widget container
        this._container.appendChild(advertisementsEl);
    }
    /**
     * Add custom markup to leaflet components.
     */
    addCustomMarkup() {
        this.addTopLeftControlsMarkup();
        this.addZoomControlsMarkup();
        this.addBottomControlsMarkup();
    }
    /**
     * Find an element in this M.A.A.P. instance's scope by it's selector.
     * @param selector - The selector to query for.
     * @returns The found child element.
     */
    querySelector(selector) {
        return this.map._container.querySelector(selector);
    }
    /**
     * Add the DOM markup for the top left controls.
     */
    addTopLeftControlsMarkup() {
        const topLeftControls = this.querySelector('.leaflet-top.leaflet-left');
        const topLeftControlsClassName = 'maap_p-top-left-controls';
        const classList = [topLeftControlsClassName];
        if (this.controls.options.isHorizontal) {
            classList.push(`${topLeftControlsClassName}--horizontal`);
        }
        if (topLeftControls) {
            addClassNames(topLeftControls, classList);
        }
    }
    /**
     * Add custom markup for zoom controls.
     */
    addZoomControlsMarkup() {
        addZoomControlMarkup(this);
    }
    /**
     * Add custom markup for bottom controls.
     */
    addBottomControlsMarkup() {
        const bottomControlsClassName = 'maap_p-bottom-controls';
        const bottomRight = this.querySelector('.leaflet-bottom.leaflet-right');
        if (!bottomRight)
            return;
        addClassNames(bottomRight, [
            bottomControlsClassName,
            `${bottomControlsClassName}--right`,
        ]);
        const bottomLeft = this.querySelector('.leaflet-bottom.leaflet-left');
        if (!bottomLeft)
            return;
        addClassNames(bottomLeft, [
            bottomControlsClassName,
            `${bottomControlsClassName}--left`,
        ]);
    }
}
//# sourceMappingURL=index.js.map